import { useState, forwardRef, useImperativeHandle } from 'react';
import { api, usePortfolio } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, X, ExternalLink, Building2 } from 'lucide-react';

const UnitSearch = forwardRef(function UnitSearch({ userRole = 'agent', onClose }, ref) {
  const { portfolios } = usePortfolio();
  
  const [isOpen, setIsOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Search filters ('any' is the sentinel since shadcn Select disallows empty-string values)
  const [bedrooms, setBedrooms] = useState('any');
  const [bathrooms, setBathrooms] = useState('any');
  const [selectedPortfolio, setSelectedPortfolio] = useState('all');
  const [statusFilter, setStatusFilter] = useState('vacant-ready');
  const [addressQuery, setAddressQuery] = useState('');

  useImperativeHandle(ref, () => ({
    openSearch: () => setIsOpen(true),
  }));

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    
    try {
      const searchParams = {};
      
      const bd = bedrooms !== 'any' ? bedrooms : '';
      const ba = bathrooms !== 'any' ? bathrooms : '';
      
      // Build BD/BA filter
      if (bd && ba) {
        searchParams.bd_ba = `${bd}/${ba}`;
      } else if (bd) {
        searchParams.bedrooms = parseInt(bd);
      }
      
      // Portfolio filter
      if (selectedPortfolio && selectedPortfolio !== 'all') {
        searchParams.portfolio_id = selectedPortfolio;
      }
      
      // Status filter (only for owners)
      if (userRole === 'owner') {
        searchParams.status_filter = statusFilter;
      }
      
      // Address filter
      if (addressQuery.trim()) {
        searchParams.address_query = addressQuery.trim();
      }
      
      const response = await api.post('/units/search', searchParams);
      setResults(response.data);
      
      if (response.data.length === 0) {
        toast.info('No units found matching your criteria');
      }
    } catch (error) {
      toast.error('Search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setBedrooms('any');
    setBathrooms('any');
    setSelectedPortfolio('all');
    setStatusFilter('vacant-ready');
    setAddressQuery('');
    setResults([]);
    setHasSearched(false);
  };

  const openSearch = () => {
    setIsOpen(true);
    clearSearch();
  };

  // Group results by portfolio
  const groupedResults = results.reduce((acc, unit) => {
    const portfolioName = unit.portfolio_name;
    if (!acc[portfolioName]) {
      acc[portfolioName] = [];
    }
    acc[portfolioName].push(unit);
    return acc;
  }, {});

  return (
    <>
      {/* Search Button */}
      <button
        onClick={openSearch}
        className="header-search-btn"
        data-testid="unit-search-button"
      >
        <Search size={18} />
        <span className="header-search-btn-label">Search Units</span>
      </button>

      {/* Search Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} data-testid="unit-search-modal">
          <DialogHeader>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Search size={20} />
              Search Units
              {userRole === 'agent' && (
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 'normal' }}>
                  (Showing Vacant-Ready only)
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Search Filters */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '16px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '12px',
            marginBottom: '16px'
          }}>
            {/* Bedrooms */}
            <div>
              <Label htmlFor="bedrooms" style={{ fontSize: '12px', color: '#64748b' }}>Bedrooms</Label>
              <Select value={bedrooms} onValueChange={setBedrooms}>
                <SelectTrigger data-testid="bedrooms-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="1">1 BR</SelectItem>
                  <SelectItem value="2">2 BR</SelectItem>
                  <SelectItem value="3">3 BR</SelectItem>
                  <SelectItem value="4">4 BR</SelectItem>
                  <SelectItem value="5">5+ BR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bathrooms */}
            <div>
              <Label htmlFor="bathrooms" style={{ fontSize: '12px', color: '#64748b' }}>Bathrooms</Label>
              <Select value={bathrooms} onValueChange={setBathrooms}>
                <SelectTrigger data-testid="bathrooms-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="1">1 BA</SelectItem>
                  <SelectItem value="1.5">1.5 BA</SelectItem>
                  <SelectItem value="2">2 BA</SelectItem>
                  <SelectItem value="2.5">2.5 BA</SelectItem>
                  <SelectItem value="3">3+ BA</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Portfolio Filter */}
            <div>
              <Label htmlFor="portfolio" style={{ fontSize: '12px', color: '#64748b' }}>Portfolio</Label>
              <Select value={selectedPortfolio} onValueChange={setSelectedPortfolio}>
                <SelectTrigger data-testid="portfolio-filter-select">
                  <SelectValue placeholder="All Portfolios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Portfolios</SelectItem>
                  {portfolios.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter (Owners only) */}
            {userRole === 'owner' && (
              <div>
                <Label htmlFor="status" style={{ fontSize: '12px', color: '#64748b' }}>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter-select">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vacant-ready">Vacant-Ready Only</SelectItem>
                    <SelectItem value="all">All Statuses</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Address Search */}
            <div style={{ gridColumn: 'span 2' }}>
              <Label htmlFor="address" style={{ fontSize: '12px', color: '#64748b' }}>Address (optional)</Label>
              <Input
                id="address"
                type="text"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                placeholder="Search by address..."
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="address-search-input"
              />
            </div>
          </div>

          {/* Search Actions */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <Button 
              onClick={handleSearch} 
              disabled={searching}
              data-testid="search-submit-button"
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
            <Button variant="outline" onClick={clearSearch}>
              <X size={16} style={{ marginRight: '4px' }} />
              Clear
            </Button>
          </div>

          {/* Results */}
          <div style={{ flex: 1, overflow: 'auto', minHeight: '200px' }}>
            {!hasSearched ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                <Search size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <p>Enter search criteria and click Search</p>
              </div>
            ) : results.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                <p>No units found matching your criteria</p>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
                  Found {results.length} unit{results.length !== 1 ? 's' : ''}
                </p>
                
                {Object.entries(groupedResults).map(([portfolioName, units]) => (
                  <div key={portfolioName} style={{ marginBottom: '24px' }}>
                    {/* Portfolio Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}>
                      <Building2 size={16} style={{ color: '#4f46e5' }} />
                      <span style={{ fontWeight: '600', color: '#3730a3' }}>{portfolioName}</span>
                      <span style={{ fontSize: '12px', color: '#6366f1' }}>({units.length} units)</span>
                    </div>
                    
                    {/* Units Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>Unit</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>Address</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>BD/BA</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>Status</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>Sqft</th>
                          {userRole === 'owner' && (
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>Vacant Status</th>
                          )}
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>Listing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {units.map((unit) => (
                          <tr 
                            key={unit.id} 
                            style={{ borderBottom: '1px solid #e2e8f0' }}
                            data-testid={`search-result-${unit.id}`}
                          >
                            <td style={{ padding: '10px 12px', fontWeight: '500' }}>{unit.unit}</td>
                            <td style={{ padding: '10px 12px', color: '#64748b', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {unit.address || '-'}
                            </td>
                            <td style={{ padding: '10px 12px' }}>{unit.bd_ba}</td>
                            <td style={{ padding: '10px 12px' }}>
                              <span className={`status-badge status-${unit.status.toLowerCase().replace(/\s+/g, '-')}`}>
                                {unit.status}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px' }}>{unit.sqft || '-'}</td>
                            {userRole === 'owner' && (
                              <td style={{ padding: '10px 12px', color: '#64748b', fontSize: '13px' }}>
                                {unit.vacant_unit_status || '-'}
                              </td>
                            )}
                            <td style={{ padding: '10px 12px' }}>
                              {unit.listing_url ? (
                                <a 
                                  href={unit.listing_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                  <ExternalLink size={14} />
                                  View
                                </a>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default UnitSearch;
